import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import {
  TrendingUp, Users, AlertTriangle, CheckCircle, Building2, Award, Clock, Flame,
  Download, X, ArrowDownCircle, BarChart3, Layers, PieChart, FileSpreadsheet, TrendingDown, AlertCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

const Tile = ({ icon: Icon, label, value, sub, color = 'text-slate-900', bg = 'bg-slate-50', testId, onClick, isActive }) => (
  <Card
    onClick={onClick}
    data-testid={testId}
    className={`border shadow-sm transition-all duration-200 ${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${
      isActive ? 'border-2 border-blue-500 ring-4 ring-blue-500/10 scale-[1.02]' : 'border-slate-200 hover:border-slate-300'
    }`}
  >
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-md ${bg} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</p>
        <p className={`text-2xl font-black ${color}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{value}</p>
        {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
        {onClick && <p className="text-[9px] text-blue-600 font-semibold mt-0.5">Click for details</p>}
      </div>
    </CardContent>
  </Card>
);

function toCSV(rows) {
  return rows.map(r => r.map(c => {
    const s = (c ?? '').toString();
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\n');
}

function downloadCSV(filename, csvText) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function CEOInsightsPanel({ fromDate, toDate, onCentreClick, centreStaffStatus }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // active detail tile key

  useEffect(() => {
    setActive(null);
    api.getCEOInsights(fromDate, toDate).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [fromDate, toDate]);

  const exportCSV = () => {
    if (!data) return;
    const ts = new Date().toISOString().split('T')[0];
    const rows = [];
    rows.push(['HIMT — CEO ROI Insights', '', '', '', '']);
    rows.push(['Period', `${data.from_date || 'All'} to ${data.to_date || 'Today'}`, '', '', '']);
    rows.push([]);
    rows.push(['KPI', 'Value', 'Sub', '', '']);
    rows.push(['Reporting Compliance', `${data.compliance.pct}%`, `${data.compliance.active} of ${data.compliance.total} staff active`, '', '']);
    rows.push(['Reports Submitted', data.throughput.submitted, '', '', '']);
    rows.push(['Reports Reviewed', data.throughput.reviewed, '', '', '']);
    rows.push(['Open Clarifications', data.open_clarifications, '', '', '']);
    rows.push(['Critical Items Flagged', data.critical_items_flagged, '', '', '']);
    rows.push(['Avg Hours per Active Staff', data.avg_hours_per_active, '', '', '']);
    rows.push(['Total Org Hours', data.hours_by_section?.total ?? 0, '', '', '']);
    rows.push([]);
    rows.push(['Hours by Section', 'Hours', '', '', '']);
    rows.push(['Academics', data.hours_by_section?.academics ?? 0, '', '', '']);
    rows.push(['Responsibilities', data.hours_by_section?.responsibilities ?? 0, '', '', '']);
    rows.push(['Initiatives', data.hours_by_section?.initiatives ?? 0, '', '', '']);
    rows.push([]);
    rows.push(['Forwarding Bottleneck', 'Count', '', '', '']);
    rows.push(['Stuck @ CIC (Submitted)', data.bottleneck?.stuck_at_cic ?? 0, '', '', '']);
    rows.push(['Stuck @ Head (CICForwarded)', data.bottleneck?.stuck_at_head ?? 0, '', '', '']);
    rows.push(['Stuck @ Principal (AHForwarded)', data.bottleneck?.stuck_at_principal ?? 0, '', '', '']);
    rows.push([]);
    rows.push(['Top Contributors', 'Hours', 'Reports', '', '']);
    (data.top_contributors || []).forEach(c => rows.push([c.name, c.hours, c.reports, '', '']));
    rows.push([]);
    rows.push(['Top Defaulters (Lowest Active Hours)', 'Hours', 'Reports', '', '']);
    (data.defaulters_low || []).forEach(c => rows.push([c.name, c.hours, c.reports, '', '']));
    rows.push([]);
    rows.push(['Non-Reporters (Zero Reports in Period)', 'Centre', 'Role', '', '']);
    (data.non_reporters || []).forEach(c => rows.push([c.name, c.centre, c.role, '', '']));
    rows.push([]);
    rows.push(['Centre Compliance Ranking', 'Compliance %', 'Active', 'Total', '']);
    (data.centre_ranking || []).forEach(c => rows.push([c.centre, c.compliance_pct, c.active, c.total, '']));
    rows.push([]);
    rows.push(['Role Contribution', 'Staff', 'Hours', 'Reports', '']);
    (data.role_contribution || []).forEach(c => rows.push([c.role, c.staff, c.hours, c.reports, '']));
    rows.push([]);
    rows.push(['Weekly Submission Trend', 'Reports', 'Hours', '', '']);
    (data.weekly_submission_trend || []).forEach(w => rows.push([w.week, w.reports, w.hours, '', '']));
    rows.push([]);
    rows.push(['Senior Mgmt Responsiveness (avg hours)', '', '', '', '']);
    rows.push(['CIC → Head', data.senior_response?.cic_to_head_hours ?? '—', '', '', '']);
    rows.push(['Head → Principal', data.senior_response?.head_to_principal_hours ?? '—', '', '', '']);
    rows.push(['Principal → CEO', data.senior_response?.principal_to_ceo_hours ?? '—', '', '', '']);
    downloadCSV(`HIMT_CEO_ROI_${ts}.csv`, toCSV(rows));
  };

  const toggle = (k) => setActive(active === k ? null : k);

  const sectionChart = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Academics', hours: data.hours_by_section?.academics ?? 0 },
      { name: 'Responsibilities', hours: data.hours_by_section?.responsibilities ?? 0 },
      { name: 'Initiatives', hours: data.hours_by_section?.initiatives ?? 0 },
    ];
  }, [data]);

  if (!data) return null;

  const {
    compliance, throughput, top_contributors, defaulters_low, non_reporters, non_reporters_count,
    centre_ranking, senior_response, open_clarifications, critical_items_flagged, critical_items,
    hours_by_section, avg_hours_per_active, bottleneck, weekly_submission_trend, role_contribution,
  } = data;

  const renderDetail = () => {
    if (!active) return null;

    const Wrap = ({ title, children, count }) => (
      <Card data-testid={`ceo-detail-${active}`} className="border-2 border-blue-500 shadow-md animate-fade-in">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">{title}</h3>
              {count !== undefined && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold">{count}</span>}
            </div>
            <button data-testid="close-ceo-detail" onClick={() => setActive(null)} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          {children}
        </CardContent>
      </Card>
    );

    if (active === 'compliance') {
      return (
        <Wrap title="Reporting Compliance Breakdown" count={`${compliance.pct}%`}>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
              <p className="text-[10px] uppercase font-bold text-emerald-700">Active Staff</p>
              <p className="text-2xl font-black text-emerald-700">{compliance.active}</p>
              <p className="text-[10px] text-slate-500">Submitted ≥1 report</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 border border-red-100">
              <p className="text-[10px] uppercase font-bold text-red-700">Inactive Staff</p>
              <p className="text-2xl font-black text-red-700">{compliance.total - compliance.active}</p>
              <p className="text-[10px] text-slate-500">No submissions in period</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-[10px] uppercase font-bold text-slate-600">Total Staff</p>
              <p className="text-2xl font-black text-slate-900">{compliance.total}</p>
              <p className="text-[10px] text-slate-500">Faculty + CIC pool</p>
            </div>
          </div>
        </Wrap>
      );
    }

    if (active === 'throughput') {
      return (
        <Wrap title="Reports Pipeline Throughput">
          <div className="grid md:grid-cols-4 gap-3">
            {[
              { label: 'Submitted', value: throughput.submitted, c: 'text-blue-700', bg: 'bg-blue-50' },
              { label: 'CIC Forwarded', value: throughput.cic_forwarded, c: 'text-indigo-700', bg: 'bg-indigo-50' },
              { label: 'Head Forwarded', value: throughput.head_forwarded, c: 'text-purple-700', bg: 'bg-purple-50' },
              { label: 'Reviewed (Final)', value: throughput.reviewed, c: 'text-emerald-700', bg: 'bg-emerald-50' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-lg p-3`}>
                <p className="text-[10px] uppercase font-bold text-slate-500">{s.label}</p>
                <p className={`text-2xl font-black ${s.c}`}>{s.value}</p>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Where reports are stuck</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-[10px] uppercase font-bold text-amber-700">Stuck @ CIC</p>
                <p className="text-2xl font-black text-amber-800">{bottleneck.stuck_at_cic}</p>
              </div>
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                <p className="text-[10px] uppercase font-bold text-orange-700">Stuck @ Head</p>
                <p className="text-2xl font-black text-orange-800">{bottleneck.stuck_at_head}</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-[10px] uppercase font-bold text-red-700">Stuck @ Principal</p>
                <p className="text-2xl font-black text-red-800">{bottleneck.stuck_at_principal}</p>
              </div>
            </div>
          </div>
        </Wrap>
      );
    }

    if (active === 'clarifications') {
      return (
        <Wrap title="Open Clarifications" count={open_clarifications}>
          <p className="text-sm text-slate-600">
            {open_clarifications} weekly summar{open_clarifications === 1 ? 'y is' : 'ies are'} currently in a clarification loop.
            Use the <span className="font-semibold text-blue-700">Weekly Summary</span> page to drill into individual cases.
          </p>
        </Wrap>
      );
    }

    if (active === 'critical') {
      return (
        <Wrap title="Critical Items Flagged" count={critical_items_flagged}>
          {(critical_items || []).length === 0 ? (
            <p className="text-xs text-slate-400 italic">No critical items flagged in this period.</p>
          ) : (
            <div className="max-h-[320px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-[10px] font-bold uppercase text-slate-500 pb-2 pr-3">Week</th>
                    <th className="text-left text-[10px] font-bold uppercase text-slate-500 pb-2 pr-3">Faculty</th>
                    <th className="text-left text-[10px] font-bold uppercase text-slate-500 pb-2">Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {critical_items.map((it, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 pr-3 text-slate-600">{it.week}</td>
                      <td className="py-2 pr-3 text-slate-700 font-medium">{it.faculty_name}</td>
                      <td className="py-2 text-slate-600">{it.activity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Wrap>
      );
    }

    if (active === 'sections') {
      return (
        <Wrap title="Org-wide Hours by Section" count={`${hours_by_section.total}h`}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sectionChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v}h`} />
              <Bar dataKey="hours" fill="#1D4ED8" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Wrap>
      );
    }

    if (active === 'avgHours') {
      return (
        <Wrap title="Avg Hours per Active Staff">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <p className="text-[10px] uppercase font-bold text-blue-700">Total Hours</p>
              <p className="text-2xl font-black text-blue-700">{hours_by_section.total}h</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
              <p className="text-[10px] uppercase font-bold text-emerald-700">Active Staff</p>
              <p className="text-2xl font-black text-emerald-700">{compliance.active}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-[10px] uppercase font-bold text-slate-700">Avg / Staff</p>
              <p className="text-2xl font-black text-slate-900">{avg_hours_per_active}h</p>
            </div>
          </div>
        </Wrap>
      );
    }

    if (active === 'weeklyTrend') {
      return (
        <Wrap title="Weekly Submission Trend (last 8 weeks)" count={`${weekly_submission_trend.length} weeks`}>
          {weekly_submission_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={weekly_submission_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="l" type="monotone" dataKey="reports" stroke="#1D4ED8" strokeWidth={2} name="Reports" />
                <Line yAxisId="r" type="monotone" dataKey="hours" stroke="#10B981" strokeWidth={2} name="Hours" />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 italic">No submissions in period.</p>}
        </Wrap>
      );
    }

    if (active === 'roles') {
      return (
        <Wrap title="Role-wise Contribution">
          {role_contribution.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No data.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-[10px] font-bold uppercase text-slate-500 pb-2 pr-3">Role</th>
                  <th className="text-right text-[10px] font-bold uppercase text-slate-500 pb-2 pr-3">Staff</th>
                  <th className="text-right text-[10px] font-bold uppercase text-slate-500 pb-2 pr-3">Reports</th>
                  <th className="text-right text-[10px] font-bold uppercase text-slate-500 pb-2">Hours</th>
                </tr>
              </thead>
              <tbody>
                {role_contribution.map(r => (
                  <tr key={r.role} className="border-b border-slate-100">
                    <td className="py-2 pr-3 text-slate-800 font-semibold">{r.role}</td>
                    <td className="py-2 pr-3 text-right text-slate-700">{r.staff}</td>
                    <td className="py-2 pr-3 text-right text-slate-700">{r.reports}</td>
                    <td className="py-2 text-right font-bold text-blue-700">{r.hours}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Wrap>
      );
    }

    if (active === 'nonReporters') {
      return (
        <Wrap title="Non-Reporters (Zero Reports)" count={non_reporters_count}>
          {non_reporters_count === 0 ? (
            <p className="text-xs text-emerald-600 italic">All staff have submitted at least one report.</p>
          ) : (
            <div className="max-h-[320px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-[10px] font-bold uppercase text-slate-500 pb-2 pr-3">Name</th>
                    <th className="text-left text-[10px] font-bold uppercase text-slate-500 pb-2 pr-3">Centre</th>
                    <th className="text-left text-[10px] font-bold uppercase text-slate-500 pb-2">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {non_reporters.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100">
                      <td className="py-2 pr-3 text-slate-800 font-medium">{s.name}</td>
                      <td className="py-2 pr-3 text-slate-600">{s.centre}</td>
                      <td className="py-2 text-slate-600">{s.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {non_reporters_count > non_reporters.length && (
                <p className="text-[10px] text-slate-400 mt-2">Showing top {non_reporters.length} of {non_reporters_count}. Export CSV for full list.</p>
              )}
            </div>
          )}
        </Wrap>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4" data-testid="ceo-insights-panel">
      {/* Header row with CSV export */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">CEO ROI Insights</h2>
        <button
          data-testid="ceo-export-csv-btn"
          onClick={exportCSV}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" />
          <Download className="w-3 h-3" />
          Export CSV
        </button>
      </div>

      {/* Hero KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Tile
          testId="ceo-compliance-tile" onClick={() => toggle('compliance')} isActive={active === 'compliance'}
          icon={CheckCircle} label="Reporting Compliance" value={`${compliance.pct}%`}
          sub={`${compliance.active} of ${compliance.total} staff active`}
          color="text-emerald-700" bg="bg-emerald-50"
        />
        <Tile
          testId="ceo-throughput-tile" onClick={() => toggle('throughput')} isActive={active === 'throughput'}
          icon={TrendingUp} label="Reports Reviewed" value={`${throughput.reviewed}`}
          sub={`${throughput.submitted} submitted · ${bottleneck.stuck_at_head} stuck @ Head`}
          color="text-blue-700" bg="bg-blue-50"
        />
        <Tile
          testId="ceo-clarifications-tile" onClick={() => toggle('clarifications')} isActive={active === 'clarifications'}
          icon={AlertTriangle} label="Open Clarifications" value={`${open_clarifications}`}
          sub="Across all weekly summaries"
          color={open_clarifications > 0 ? 'text-amber-700' : 'text-slate-700'}
          bg={open_clarifications > 0 ? 'bg-amber-50' : 'bg-slate-50'}
        />
        <Tile
          testId="ceo-critical-tile" onClick={() => toggle('critical')} isActive={active === 'critical'}
          icon={Flame} label="Critical Items Flagged" value={`${critical_items_flagged}`}
          sub="By Heads (this period)"
          color={critical_items_flagged > 0 ? 'text-red-700' : 'text-slate-700'}
          bg={critical_items_flagged > 0 ? 'bg-red-50' : 'bg-slate-50'}
        />
        <Card data-testid="ceo-centre-status-tile" onClick={onCentreClick} className="border border-slate-200 shadow-sm cursor-pointer hover:border-blue-400 hover:shadow-md transition-all">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-700" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Centre Staff Updating</p>
              <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <span className="text-emerald-600">{centreStaffStatus?.active ?? '—'}</span>
                <span className="text-slate-400 text-base"> / {centreStaffStatus?.total ?? '—'}</span>
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">{centreStaffStatus?.inactive ?? 0} not updating · <span className="text-blue-600 font-semibold">Details</span></p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second KPI row — new metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          testId="ceo-org-hours-tile" onClick={() => toggle('sections')} isActive={active === 'sections'}
          icon={Layers} label="Total Org Hours" value={`${hours_by_section.total}h`}
          sub={`Acad ${hours_by_section.academics}h · Resp ${hours_by_section.responsibilities}h · Init ${hours_by_section.initiatives}h`}
          color="text-indigo-700" bg="bg-indigo-50"
        />
        <Tile
          testId="ceo-avg-hours-tile" onClick={() => toggle('avgHours')} isActive={active === 'avgHours'}
          icon={BarChart3} label="Avg Hours / Active Staff" value={`${avg_hours_per_active}h`}
          sub={`Across ${compliance.active} active staff`}
          color="text-slate-900" bg="bg-slate-100"
        />
        <Tile
          testId="ceo-weekly-trend-tile" onClick={() => toggle('weeklyTrend')} isActive={active === 'weeklyTrend'}
          icon={TrendingUp} label="Weekly Trend" value={`${weekly_submission_trend.length}w`}
          sub="Last 8 weeks of submissions"
          color="text-blue-700" bg="bg-blue-50"
        />
        <Tile
          testId="ceo-nonreporters-tile" onClick={() => toggle('nonReporters')} isActive={active === 'nonReporters'}
          icon={AlertCircle} label="Non-Reporters" value={`${non_reporters_count}`}
          sub="Staff with 0 reports in period"
          color={non_reporters_count > 0 ? 'text-red-700' : 'text-emerald-700'}
          bg={non_reporters_count > 0 ? 'bg-red-50' : 'bg-emerald-50'}
        />
      </div>

      {/* Detail panel */}
      {renderDetail()}

      {/* Two-column: Top Contributors + Top Defaulters */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">Top Contributors (Hours)</h3>
            </div>
            {top_contributors && top_contributors.length > 0 ? (
              <div className="space-y-2">
                {top_contributors.map((c, i) => (
                  <div key={c.id} data-testid={`ceo-top-${i}`} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${i === 0 ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-700'}`}>#{i + 1}</span>
                      <span className="text-sm font-medium text-slate-800">{c.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-slate-900">{c.hours}h</p>
                      <p className="text-[10px] text-slate-500">{c.reports} reports</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-slate-400 italic">No data in selected period</p>}
          </CardContent>
        </Card>

        <Card className="border border-red-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-red-700">Defaulters (Lowest Active Hours)</h3>
            </div>
            {defaulters_low && defaulters_low.length > 0 ? (
              <div className="space-y-2">
                {defaulters_low.map((c, i) => (
                  <div key={c.id} data-testid={`ceo-defaulter-${i}`} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black bg-red-100 text-red-700">↓{i + 1}</span>
                      <span className="text-sm font-medium text-slate-800">{c.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-red-700">{c.hours}h</p>
                      <p className="text-[10px] text-slate-500">{c.reports} reports</p>
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-slate-400 italic mt-2">Lowest-hours staff who DID submit ≥1 report. Non-reporters listed in dedicated KPI tile above.</p>
              </div>
            ) : <p className="text-xs text-slate-400 italic">No active reporters in period</p>}
          </CardContent>
        </Card>
      </div>

      {/* Centre Compliance Ranking */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">Centre Compliance Ranking</h3>
          </div>
          {centre_ranking && centre_ranking.length > 0 ? (
            <div className="space-y-2">
              {centre_ranking.slice(0, 10).map((c, i) => (
                <div key={c.centre} data-testid={`ceo-centre-${i}`} className="flex items-center gap-3 border-b border-slate-100 last:border-0 py-1.5">
                  <span className="text-sm font-medium text-slate-800 w-28 truncate">{c.centre}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${c.compliance_pct >= 70 ? 'bg-emerald-500' : c.compliance_pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${c.compliance_pct}%` }}></div>
                  </div>
                  <span className="text-xs font-bold text-slate-700 w-16 text-right">{c.compliance_pct}%</span>
                  <span className="text-[10px] text-slate-400 w-14 text-right">{c.active}/{c.total}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400 italic">No centres</p>}
        </CardContent>
      </Card>

      {/* Role contribution + Senior responsiveness */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="ceo-role-contribution-card" onClick={() => toggle('roles')} className="border border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-all">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <PieChart className="w-4 h-4 text-indigo-600" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">Role-wise Contribution</h3>
              <span className="text-[10px] text-blue-600 font-semibold ml-auto">Click for details</span>
            </div>
            {role_contribution.length > 0 ? (
              <div className="space-y-1.5">
                {role_contribution.slice(0, 6).map((r) => (
                  <div key={r.role} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                    <span className="font-semibold text-slate-700">{r.role}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">{r.staff} staff</span>
                      <span className="text-sm font-bold text-blue-700 w-16 text-right">{r.hours}h</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-slate-400 italic">No data</p>}
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-indigo-600" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">Senior Mgmt Responsiveness</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase font-bold text-slate-500">CIC → Head</p>
                <p className="text-2xl font-black text-slate-900">{senior_response.cic_to_head_hours ?? '—'}<span className="text-sm text-slate-400 ml-0.5">h</span></p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase font-bold text-slate-500">Head → Principal</p>
                <p className="text-2xl font-black text-slate-900">{senior_response.head_to_principal_hours ?? '—'}<span className="text-sm text-slate-400 ml-0.5">h</span></p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase font-bold text-slate-500">Principal → CEO</p>
                <p className="text-2xl font-black text-slate-900">{senior_response.principal_to_ceo_hours ?? '—'}<span className="text-sm text-slate-400 ml-0.5">h</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
